// Cohortes et échantillons d'un projet (modèle v2) : projects/{projectId}/cohorts, projects/{projectId}/samples.
import {
  addDoc, collection, deleteDoc, deleteField, doc, getDoc, getDocs, onSnapshot, orderBy, query, runTransaction,
  serverTimestamp, updateDoc,
} from 'firebase/firestore';
import { deleteObject, ref } from 'firebase/storage';
import { db, storage } from './firebase';
import { canWriteProject, docData } from './projects';
import { deleteImage } from './images';

export const SAMPLE_STATUS = { draft: 'Draft', ready: 'Ready' };

// Réglages du viewer partagés entre utilisateurs (samples/{id}.comparison) : canal calculé, modalité affichée, rideau,
// ordre des modalités, recadrage, zones effacées (docs/DATA_MODEL.md).
export const saveSampleComparison = (projectId, sampleId, comparison) =>
  updateDoc(doc(db(), 'projects', projectId, 'samples', sampleId), { comparison, updatedAt: serverTimestamp() });

// Taille de pixel de l'échantillon (metadata.pixelSizeUm, µm, grille commune des images) ; null : la retirer.
export const setSamplePixelSize = (projectId, sampleId, um) =>
  updateDoc(doc(db(), 'projects', projectId, 'samples', sampleId), {
    'metadata.pixelSizeUm': um || deleteField(), updatedAt: serverTimestamp(),
  });

// Nom d'un canal calculé (comparison.labels.{id}), enregistré tout de suite ; null : nom par défaut.
export const renameComputedChannel = (projectId, sampleId, id, label) =>
  updateDoc(doc(db(), 'projects', projectId, 'samples', sampleId), {
    [`comparison.labels.${id}`]: label || deleteField(), updatedAt: serverTimestamp(),
  });

// Modalité supprimée : retire ses traces des réglages du viewer (A / B du canal calculé, modalité affichée, ordre,
// canaux de calcul rangés, zones effacées), dans une transaction (le document a pu changer depuis son affichage).
export const forgetImageInSample = (projectId, sampleId, imageId) => runTransaction(db(), async tx => {
  const ref = doc(db(), 'projects', projectId, 'samples', sampleId);
  const comparison = (await tx.get(ref)).data()?.comparison;
  if (!comparison) return;
  const next = { ...comparison };
  ['a', 'b', 'active'].forEach(k => { if (next[k] === imageId) next[k] = null; });
  if (next.collagen?.c === imageId) next.collagen = { ...next.collagen, c: null };
  if (next.vhe) next.vhe = Object.fromEntries(Object.entries(next.vhe).map(([k, v]) => [k, ['n', 'r', 'e', 'c'].includes(k) && v === imageId ? null : v]));
  if (next.nuclei) next.nuclei = { ...next.nuclei, n: next.nuclei.n === imageId ? null : next.nuclei.n, r: next.nuclei.r === imageId ? null : next.nuclei.r };
  if (next.composite) next.composite = Object.fromEntries(Object.entries(next.composite).map(([k, v]) => [k, ['r', 'g', 'b'].includes(k) && v === imageId ? null : v]));
  if (next.redox) next.redox = { ...next.redox, n: next.redox.n === imageId ? null : next.redox.n, f: next.redox.f === imageId ? null : next.redox.f };
  if (next.blood) next.blood = { ...next.blood, s: next.blood.s === imageId ? null : next.blood.s, r: next.blood.r === imageId ? null : next.blood.r };
  if (Array.isArray(next.order)) next.order = next.order.filter(id => id !== imageId);
  if (Array.isArray(next.stash)) next.stash = next.stash.filter(id => id !== imageId);
  if (Array.isArray(next.erase)) next.erase = next.erase.filter(z => z.t !== imageId || (z.bg && z.bg !== 'he'));  // zones partagées gardées
  tx.update(ref, { comparison: next, updatedAt: serverTimestamp() });
});

export function watchCohorts(projectId, onChange, onError) {
  return onSnapshot(query(collection(db(), 'projects', projectId, 'cohorts'), orderBy('label')),
    snap => onChange(snap.docs.map(docData)), onError);
}

export const renameCohort = (projectId, cohortId, label) =>
  updateDoc(doc(db(), 'projects', projectId, 'cohorts', cohortId), { label: label.trim() });

export async function createCohort(profile, projectId, { label, description = '' }) {
  const ref = await addDoc(collection(db(), 'projects', projectId, 'cohorts'), {
    label: label.trim(), description: description.trim(), createdBy: profile.uid, createdAt: serverTimestamp(),
  });
  return ref.id;
}

// Échantillons du projet, hors corbeille, les plus récents d'abord.
export function watchSamples(projectId, onChange, onError) {
  return onSnapshot(query(collection(db(), 'projects', projectId, 'samples'), orderBy('createdAt', 'desc')),
    snap => onChange(snap.docs.map(docData).filter(s => s.status !== 'trashed')), onError);
}

// Nouveau nom d'un échantillon ; s'il est une démo, nom de la liste des démos aussi (administrateurs seulement : pour les
// autres, la liste garde l'ancien nom jusqu'à ce qu'un administrateur renomme l'échantillon ou refasse la démo).
export async function renameSample(profile, projectId, sampleId, name) {
  await updateDoc(doc(db(), 'projects', projectId, 'samples', sampleId), { name: name.trim(), updatedAt: serverTimestamp() });
  if (profile.role !== 'admin') return;
  const demo = await getDoc(doc(db(), 'demos', sampleId));
  if (demo.exists()) await updateDoc(demo.ref, { name: name.trim() });
}

// Peut supprimer l'échantillon (mêmes conditions que firestore.rules) : administrateur, ou son auteur s'il peut écrire
// dans le projet (les images d'autres auteurs le bloquent alors : deleteSample).
export const canDeleteSample = (profile, project, sample) => !!profile && !!sample && (
  profile.role === 'admin' || (canWriteProject(profile, project) && sample.createdBy === profile.uid)
);

// Suppression définitive d'un échantillon et de ses données : images (fiche et fichier d'origine ; le serveur efface
// ensuite leurs tuiles, functions/main.py onimagedelete), pièces jointes, cartes d'attention, démo, puis sa fiche
// (réglages du viewer compris). Droits vérifiés avant de commencer, pour ne pas laisser un échantillon à moitié supprimé.
export async function deleteSample(profile, projectId, sampleId) {
  const sampleRef = doc(db(), 'projects', projectId, 'samples', sampleId);
  const [images, attachments, overlays, demo] = await Promise.all([
    getDocs(collection(sampleRef, 'images')), getDocs(collection(sampleRef, 'attachments')),
    getDocs(collection(sampleRef, 'overlays')), getDoc(doc(db(), 'demos', sampleId)),
  ]);
  if (profile.role !== 'admin') {
    if (demo.exists()) throw new Error('This sample is a demo: only an administrator can delete it.');
    const others = [...images.docs, ...attachments.docs, ...overlays.docs].filter(d => d.data().createdBy !== profile.uid);
    if (others.length) throw new Error('Some images or maps were added by other users: only an administrator can delete this sample.');
  }
  for (const d of images.docs) await deleteImage(projectId, sampleId, docData(d));
  for (const d of attachments.docs) {
    const { storagePath } = d.data();
    if (storagePath) {
      await deleteObject(ref(storage(), storagePath)).catch(err => {
        if (err.code !== 'storage/object-not-found') throw err;
      });
    }
    await deleteDoc(d.ref);
  }
  for (const d of overlays.docs) await deleteDoc(d.ref);
  if (demo.exists()) await deleteDoc(demo.ref);
  await deleteDoc(sampleRef);
}

export async function createSample(profile, projectId, { name, cohortId = '', description = '' }) {
  const ref = await addDoc(collection(db(), 'projects', projectId, 'samples'), {
    name: name.trim(), cohortId, description: description.trim(), status: 'draft', metadata: {},
    createdBy: profile.uid, createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
  });
  return ref.id;
}
